import { request, language, CustomException, cache } from '@tencent/ppd-base-libs';
import { Block, Information, Tag, ActivityCfg, Banner, Ad, ABTestDivertCfg, ActBanner, BannerActivityCfg } from '@/types/index';
import moment from 'moment';
import { cacheFunc } from '@tencent/wphk-base-utils';

const qryDiscountCenterV3Cfg = cacheFunc(async () => request.loadVtools('https://prom.wechatpay.com.hk/fmd_mojo/ppd_config/discountCenterV3Cfg/config.json'));

type LocalDiscountCenterCfg = {
  value: any;
  time: number;
};
const DISCOUNT_CENTER_CFG = 'DISCOUNT_CENTER_CFG';
const DISCOUNT_CENTER_CFG_TIME = 600000;

/**
 * 读取优惠专区配置缓存
 * 强制缓存 10min
 */
function getDiscountCenterCfg(): LocalDiscountCenterCfg | null {
  try {
    const localData = cache.getItemSync(DISCOUNT_CENTER_CFG);
    if (!localData) return null;
    const { value } = JSON.parse(localData);
    if (!value) return null;
    return value;
  } catch (error) {
    return null;
  }
};
/**
 * 设置优惠专区配置缓存
 * @param rawData
 */
function setDiscountCenterCfg(rawData) {
  const localData = {
    value: rawData,
  };
  cache.setItemSync(DISCOUNT_CENTER_CFG, JSON.stringify(localData), DISCOUNT_CENTER_CFG_TIME);
}

type LangKey = 'en' | 'zh_hk';
/**
 * 根据当前语言环境获取配置中的多语言，默认只有繁英
 */
function getLangAttr(): LangKey {
  const curLang = language.getLanguage().toLowerCase()
    .replace(/-/g, '_');
  const lang = ['en', 'zh_hk'].includes(curLang) ? curLang : 'zh_hk';
  return lang;
}

function formatTimeStr(str: string) {
  if (!str) return '';
  return str.replace(/-/g, '/');
}

/**
 * 获取配置内容
 */
export async function getFormatedDiscountCenterCfg() {
  const localData: LocalDiscountCenterCfg | null = getDiscountCenterCfg();
  const rawData = localData || await qryDiscountCenterV3Cfg();
  if (!localData && rawData) setDiscountCenterCfg(rawData);

  const lang = getLangAttr();

  // 格式化区块配置
  const discountCenterMoudleCfg = formatDiscountCenterMoudleCfg(rawData.discountCenterMoudleCfg, lang);

  // 资讯模块配置
  const discountCenterInfoCfg = formatDiscountCenterInfoCfg(rawData.discountCenterInfoCfg, lang);

  // 新版banner样式活动列表配置
  const dsicountCenterBannerActivityCfg = formatBannerActivityCfg(rawData.discountCenterBannerActivityCfg, lang);

  // 活动列表配置
  const dsicountCenterActivityCfg = formatDiscountCenterActivityCfg(rawData.discountCenterActivityCfg, lang);
  // 活动列表中banner图配置
  const discountCenterActivityBannerCfg = formatActBanner(rawData.discountCenterActivityBannerCfg, lang);
  // banner配置
  const discountCenterBannerCfg = formatDiscountCenterBannerCfg(rawData.discountCenterBannerCfg, lang);

  // 巨幕banner配置
  const discountCenterMaxAdCfg = formatDiscountCenterMaxAdCfg(rawData.discountCenterMaxAdCfg, lang);

  // 巨幕bannerABT实验配置
  const abtTestDivertCfg = formatABTestCfg(rawData.ABTestDivertCfg);


  return {
    discountCenterMoudleCfg,
    discountCenterInfoCfg,
    dsicountCenterActivityCfg,
    discountCenterBannerCfg,
    discountCenterMaxAdCfg,
    abtTestDivertCfg,
    discountCenterActivityBannerCfg,
    dsicountCenterBannerActivityCfg,
  };
}

/**
 * 格式化区块配置
 */
function formatDiscountCenterMoudleCfg(rawData, lang: LangKey) {
  // 格式化区块配置
  const discountCenterMoudleCfg: Block[] = rawData.map((module) => {
    // 前端配置系统里面自定义结构体类型的字段在返回值里面的表现是数组，所以需要取数组的第一项值
    const { blockId, navLink, navText, sort, title } = module;
    return {
      blockId,
      navLink,
      navText: navText[0] ? navText[0][lang] : '',
      sort: +sort,
      title: title[0] ? title[0][lang] : '',
    };
  });
  console.log('discountCenterMoudleCfg', discountCenterMoudleCfg);
  return discountCenterMoudleCfg;
}

/**
 * 资讯模块配置
 */
function formatDiscountCenterInfoCfg(rawData, lang: LangKey) {
  let discountCenterInfoCfg: Information[] = [];
  // 资讯模块配置
  try {
    discountCenterInfoCfg = rawData.map((info) => {
      const { title, url, time, tagList, order, logo, desc, id } = info;
      const { startTime, endTime } = time[0];
  
      const start = moment(new Date(formatTimeStr(startTime))).toDate();
      const end = moment(new Date(formatTimeStr(endTime))).toDate();
  
      const tags = formatTagList(tagList, lang);

      return {
        title: title[0] ? title[0][lang] : '',
        url,
        order: +order,
        logo,
        desc: desc[0] ? desc[0][lang] : '',
        id,
        startTime: start,
        endTime: end,
        tagList: tags,
      };
    });
  } catch (error) {
    new CustomException(error, 'formatDiscountCenterInfoCfgErr', '资讯模块配置格式化报错')
  }
  console.log('discountCenterInfoCfg', discountCenterInfoCfg);
  return discountCenterInfoCfg;
}

/**
 * 活动列表模块格式化
 */
function formatDiscountCenterActivityCfg(rawData, lang: LangKey) {
  // 活动列表配置
  let discountCenterActivityCfg: ActivityCfg[] = [];

  try {
    discountCenterActivityCfg = rawData.map((act) => {
      const { id, bgColor, desc, logo, moduleId, sort, tagList, time, title, url, checkCfg: checkCfgArr } = act;
      const { startTime, endTime, isShowLeft } = time[0];
      const checkCfg = checkCfgArr[0];
  
      const tags = formatTagList(tagList, lang);
  
      const start = moment(new Date(formatTimeStr(startTime))).toDate();
      const end = moment(new Date(formatTimeStr(endTime))).toDate();
  
      return {
        activityData: {
          id,
          title: title[0] ? title[0][lang] : '',
          desc: desc[0] ? desc[0][lang] : '',
          bgColor: `#${bgColor}`,
          logo,
          url,
          tagList: tags,
          startTime: start,
          endTime: end,
          isShowLeft: +isShowLeft === 1,
        },
        sort: +sort,
        blockId: moduleId,
        checkCfg,
      };
    });
  } catch (error) {
    new CustomException(error, 'formatDiscountCenterActivityCfgErr', '活动列表模块格式化报错')
  }


  console.log('discountCenterActivityCfg', discountCenterActivityCfg);
  return discountCenterActivityCfg;
}
/**
 * 新版banner活动列表模块格式化
 */
function formatBannerActivityCfg(rawData, lang: LangKey) {
  let discountCenterBannerActivityCfg: BannerActivityCfg[] = [];
  // 活动列表配置
  try {
    discountCenterBannerActivityCfg = rawData.map((act) => {
      const { id, title, moduleId, sort, time, url, checkCfg: checkCfgArr, banner } = act;
      const { startTime, endTime, type } = time[0];
      const checkCfg = checkCfgArr[0];
      const img = banner[0];
  
      const start = moment(new Date(formatTimeStr(startTime))).toDate();
      const end = moment(new Date(formatTimeStr(endTime))).toDate();
  
      return {
        id,
        title: title[0] ? title[0][lang] : '',
        img: img ? img[lang] : '',
        url,
        sort: +sort,
        blockId: moduleId,
        checkCfg,
        startTime: start,
        endTime: end,
        descTimeType: +type,
      };
    });
  } catch (error) {
    new CustomException(error, 'formatBannerActivityCfgErr', '新版banner活动列表模块格式化报错')
  }
  console.log('discountCenterBannerActivityCfg', discountCenterBannerActivityCfg);
  return discountCenterBannerActivityCfg;
}

/** 格式化标签列表 */
function formatTagList(tags: {
  zh_hk: string,
  en: string,
  type: string
}[], lang: LangKey) {
  const tagList = tags.map((tag) => {
    // 当是英文环境时，且没配置文案，则不展示
    if (lang === 'en' && !tag.en) return;
    return {
      text: tag[lang],
      type: tag.type,
    };
  }).filter(tag => !!tag) as Tag[];
  return tagList;
}

/**
 * 格式化banner配置
 */
function formatDiscountCenterBannerCfg(rawData, lang: LangKey) {
  let discountCenterBannerCfg: Banner[] = [];
  // 格式化区块配置
  try {
    discountCenterBannerCfg = rawData.map((banner) => {
      // 前端配置系统里面自定义结构体类型的字段在返回值里面的表现是数组，所以需要取数组的第一项值
      const { img, url, sort, startTime, endTime, id } = banner;
  
      const start = moment(new Date(formatTimeStr(startTime))).toDate();
      const end = moment(new Date(formatTimeStr(endTime))).toDate();
  
      return {
        id,
        img: img[0] ? img[0][lang] : '',
        url,
        startTime: start,
        endTime: end,
        sort,
      };
    });
  } catch (error) {
    new CustomException(error, 'formatDiscountCenterBannerCfgErr', '格式化banner配置报错')
  }
  console.log('discountCenterBannerCfg', discountCenterBannerCfg);
  return discountCenterBannerCfg;
}

/**
 * 格式化大屏广告配置
 */
function formatDiscountCenterMaxAdCfg(rawData, lang: LangKey) {
  let discountCenterMaxAdCfg: Ad[] = [];
  // 格式化区块配置
  try {
    discountCenterMaxAdCfg = rawData.map((banner) => {
      // 前端配置系统里面自定义结构体类型的字段在返回值里面的表现是数组，所以需要取数组的第一项值
      const {
        maxImg,
        adType,
        adBanner,
        title,
        logo,
        desc,
        btnText,
        url,
        largeTime,
        cutdown,
        isShowLarge,
        showLargePeriod,
        largePeriodShowLimit,
        daliyShowCnt,
        smallTime,
        id,
        abtestGroup,
        backBtnColor,
      } = banner;
      const { startTime: largeStartTime, endTime: largeEndTime } = largeTime[0] || {};
      const { startTime: smallStartTime, endTime: smallEndTime } = smallTime[0] || {};
  
      const largeStart = moment(new Date(formatTimeStr(largeStartTime))).toDate();
      const largeEnd = moment(new Date(formatTimeStr(largeEndTime))).toDate();
      const smallStart = moment(new Date(formatTimeStr(smallStartTime))).toDate();
      const smallEnd = moment(new Date(formatTimeStr(smallEndTime))).toDate();
  
      return {
        id: +id,
        adType: +adType,
        maxImg: maxImg[0] ? maxImg[0][lang] : '',
        cutdown: +cutdown,
        adBanner,
        title: title[0] ? title[0][lang] : '',
        desc: desc[0] ? desc[0][lang] : '',
        btnText: btnText[0] ? btnText[0][lang] : '',
        url,
        logo,
        largeStartTime: largeStart,
        largeEndTime: largeEnd,
        isShowLarge: +isShowLarge === 1,
        showLargePeriod: +showLargePeriod,
        largePeriodShowLimit: +largePeriodShowLimit,
        daliyShowCnt: +daliyShowCnt,
        smallStartTime: smallStart,
        smallEndTime: smallEnd,
        abtestGroup,
        backBtnColor,
      };
    });
  } catch (error) {
    new CustomException(error, 'formatDiscountCenterMaxAdCfgErr', '格式化大屏广告配置报错')
  }
  console.log('discountCenterMaxAdCfg', discountCenterMaxAdCfg);
  return discountCenterMaxAdCfg;
}

/**
 * 格式化abtest分流配置
 */
function formatABTestCfg(rawData): ABTestDivertCfg {
  const cfg = rawData[0];
  if (!cfg) return Object();

  const {
    abtStartTime,
    abtEndTime,
    abtestGroupConfig,
    abtModuleSet,
  } = cfg;

  const actStart = moment(new Date(formatTimeStr(abtStartTime))).toDate();
  const actEnd = moment(new Date(formatTimeStr(abtEndTime))).toDate();

  const groups = abtestGroupConfig.map((item) => {
    const {
      startTime,
      endTime,
      groupData,
    } = item;

    const start = moment(new Date(formatTimeStr(startTime))).toDate();
    const end = moment(new Date(formatTimeStr(endTime))).toDate();

    // const rules = groupData.map(rule => {
    // 	const { group, abtestID = '' } = rule;
    // 	return {
    // 		group,
    // 		abtestID: abtestID.split(','),
    // 	}
    // })
    return {
      startTime: start,
      endTime: end,
      groupData,
    };
  });
  return {
    abtStartTime: actStart,
    abtEndTime: actEnd,
    abtModuleSet,
    abtestGroupConfig: groups,
  };
}

/**
 * 格式化活动列表banner
 * @param discountCenterActivityBannerCfg
 * @param lang
 */
function formatActBanner(rawData, lang: LangKey) {
  let discountCenterActivityBannerCfg: ActBanner[] = [];
  try {
    discountCenterActivityBannerCfg = rawData.map((item) => {
      const {
        blockId,
        url,
        img,
        id,
        startTime,
        endTime,
      } = item;
      const start = moment(new Date(formatTimeStr(startTime))).toDate();
      const end = moment(new Date(formatTimeStr(endTime))).toDate();
      return {
        blockId,
        id,
        url,
        startTime: start,
        endTime: end,
        img: img[0] ? img[0][lang] : '',
      };
    });
  } catch (error) {
    new CustomException(error, 'formatActBannerErr', '格式化活动列表banner报错')
  }
  return discountCenterActivityBannerCfg;
}